'use client';

import { useMemo } from 'react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import hash from 'hash-object';
import { createExpandColumn, createSelectionColumn } from '../data-table/factories';
import useSearchParamPagination from './useSearchParamPagination';
import useTableSorting from './useTableSorting';
import { useActiveRow } from './useActiveRow';
import { useClientTableModel } from './useClientTableModel';
import { rowIdResetKey, useRowExpansion } from './useRowExpansion';
import { useRowSelection } from './useRowSelection';
import { toGetRowIdOption, withRowId } from './rowIdentity';
import type { Pagination, TableToolbarContext } from '../data-table/types';
import type { GetRowId } from './rowIdentity';
import type { ActiveRowConfig } from './useActiveRow';
import type { ClientDataConfig, ResolvedClientDataConfig } from './useClientTableModel';
import type { RowExpansionConfig } from './useRowExpansion';
import type { RowSelectionConfig } from './useRowSelection';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
interface UseDataTableBaseConfig<TData> {
  data: TData[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];
  pagination?: Pagination;
  /**
   * Initial sort state, and the mapping from column id to the API's sort key.
   * `sortKeyMap` is ignored under `clientData`, where TanStack sorts the loaded
   * rows in the browser and no query carries a sort key.
   */
  sorting?: {
    initialSortState?: SortingState;
    sortKeyMap?: Record<string, string>;
  };
  /** Label for the total count display in pagination (e.g., 'customers', 'payments') */
  totalCountLabel?: string;
  /** Sort and search the already-fetched rows in the browser. */
  clientData?: ClientDataConfig<TData>;
}
interface RowIdentityConfig<TData> {
  /**
   * Stable id per row, shared by row selection, row expansion and the active row.
   * Required as soon as one of them is enabled.
   */
  getRowId: GetRowId<TData>;
}

/** Features whose state is keyed by row, and so require the table's `getRowId`. */
interface RowKeyedFeatures<TData, TChildRow> {
  /** Enable native multi-row selection (prepends a checkbox column). */
  rowSelection?: RowSelectionConfig<TData>;
  /** Enable a single active row for master/detail views. */
  activeRow?: ActiveRowConfig;
  /** Enable row expansion (prepends a chevron column). */
  rowExpansion?: RowExpansionConfig<TData, TChildRow>;
}

/** Every row-keyed feature shares one row identity, so none of them can key its state differently. */
type UseDataTableConfig<TData, TChildRow> = UseDataTableBaseConfig<TData> & ((RowIdentityConfig<TData> & RowKeyedFeatures<TData, TChildRow>) | ({
  getRowId?: never;
} & { [K in keyof RowKeyedFeatures<TData, TChildRow>]?: never }));

/**
 * Centralized hook for TanStack Table with automatic data-based reactivity.
 * Combines common table setup patterns including pagination and sorting.
 *
 * @param config - Table configuration
 * @returns DataTable value props and table controls
 *
 * @example
 * // Simple table with no pagination or sorting
 * const table = useDataTable({
 *   data: items,
 *   columns: COLUMNS,
 * });
 * return <DataTable {...table} />;
 *
 * @example
 * // Table with pagination
 * const table = useDataTable({
 *   data: items,
 *   columns: COLUMNS,
 *   pagination: paginationData,
 * });
 * return <DataTable {...table} />;
 *
 * @example
 * // Table with pagination and sorting
 * const table = useDataTable({
 *   data: items,
 *   columns: COLUMNS,
 *   pagination: paginationData,
 *   sorting: {
 *     initialSortState: [{ id: 'createdAt', desc: true }],
 *     sortKeyMap: { createdAt: 'created_at' },
 *   },
 * });
 * return <DataTable {...table} />;
 *
 * @example
 * // Master/detail table: the clicked row stays highlighted and `activeRow` is
 * // the resolved row object for the detail pane.
 * const { activeRow, activeRowState, headerGroups, rowModel } = useDataTable({
 *   data: items,
 *   columns: COLUMNS,
 *   getRowId: (row) => row.id,
 *   activeRow: { initial: 'first' },
 * });
 * return <DataTable activeRowState={activeRowState} headerGroups={headerGroups} rowModel={rowModel} />;
 */
export default function useDataTable<TData, TChildRow = never>({
  data,
  columns,
  pagination,
  sorting: sortingConfig,
  totalCountLabel,
  getRowId,
  rowSelection,
  activeRow,
  rowExpansion,
  clientData
}: UseDataTableConfig<TData, TChildRow>) {
  // Generate stable key based on data content
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dataKey = useMemo(() => hash(data as Record<string, any>), [data]);
  const rowIdOption = getRowId ? toGetRowIdOption(getRowId) : undefined;
  const clientDataConfig: ResolvedClientDataConfig<TData> | undefined = clientData ? {
    ...clientData,
    ...(sortingConfig?.initialSortState && {
      initialSorting: sortingConfig.initialSortState
    })
  } : undefined;

  // Always call hooks unconditionally (React Rules of Hooks), use defaults when not configured
  const defaultPagination: Pagination = {
    cursors: {}
  };
  const defaultSorting = {
    initialSortState: [] as SortingState,
    sortKeyMap: {}
  };
  const paginationResult = useSearchParamPagination(pagination ?? defaultPagination);
  const urlSortingResult = useTableSorting(sortingConfig ?? defaultSorting);
  const client = useClientTableModel(clientDataConfig);
  const paginationConfig = pagination ? paginationResult : undefined;
  // Client mode owns the sort state itself, so the URL sort hook stays out of the table.
  const urlSorting = sortingConfig && !client.enabled ? urlSortingResult : undefined;

  // Selection tracks the currently-loaded rows: `dataKey` (a hash of the data)
  // changes when the page/filter changes, resetting the selection.
  const selection = useRowSelection(rowSelection, dataKey);
  // Expansion resets on the row set, not on row content: a table whose rows are mutated in
  // place (a per-row progress or status field) would otherwise collapse under the user.
  const expansionConfig = withRowId(rowExpansion, getRowId);
  const expansion = useRowExpansion(expansionConfig, expansionConfig ? rowIdResetKey(data, expansionConfig.getRowId) : dataKey);

  // TanStack memoizes its whole column model on the identity of this array, so a table with
  // no feature column passes the caller's array straight through and keeps that memo.
  const tableColumns = rowSelection || rowExpansion ? [...(rowSelection ? [createSelectionColumn<TData>({
    getDisabledReason: rowSelection.rowSelectionDisabledReason
  })] : []), ...(rowExpansion ? [createExpandColumn<TData>()] : []), ...columns] : columns;

  // Initialize table
  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      ...(urlSorting && {
        sorting: urlSorting.sorting
      }),
      ...(client.enabled && {
        sorting: client.sorting,
        globalFilter: client.globalFilter
      }),
      ...(selection.enabled && {
        rowSelection: selection.rowSelectionState
      }),
      ...(expansion.enabled && {
        expanded: expansion.expandedState
      })
    },
    ...(urlSorting && {
      onSortingChange: urlSorting.setSorting
    }),
    ...(rowIdOption && {
      getRowId: rowIdOption
    }),
    ...selection.tableOptions,
    ...expansion.tableOptions,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    ...client.tableOptions,
    getCoreRowModel: getCoreRowModel()
  });

  // The rows on screen, which `clientData` sorting and searching reorder and narrow, so the
  // active row resolves against what the user sees rather than the order `data` arrived in.
  const visibleRows = table.getRowModel().rows.map(row => row.original);
  const active = useActiveRow(withRowId(activeRow, getRowId), visibleRows);
  const selectedRows = selection.getSelectedRows(table);
  const toggleRow = (row_0: TData) => expansion.toggleRow(table, row_0);
  return {
    // The TanStack instance, for a caller-rendered toolbar that needs row counts or
    // expand/collapse-all (the same access createTableSuspenseWrapper's toolbar slot gets).
    tableInstance: table,
    // Ready-made context for a caller-rendered toolbar (search box, expand/collapse-all, count),
    // mirroring the toolbar ctx createTableSuspenseWrapper builds. Saves every call site
    // re-deriving it, and folds the "is clientData on?" decision into one place so a toolbar
    // search box can't be silently inert. `toggleRow` is a no-op unless `rowExpansion` is set,
    // and `isFetching` is false since a `useDataTable` caller owns its own data.
    toolbarContext: {
      table,
      globalFilter: client.enabled ? client.globalFilter : '',
      onGlobalFilterChange: client.enabled ? client.setGlobalFilter : () => {},
      isFetching: false,
      toggleRow
    } satisfies TableToolbarContext<TData>,
    headerGroups: table.getHeaderGroups(),
    rowModel: table.getRowModel(),
    ...(paginationConfig && {
      pagination: {
        ...paginationConfig,
        ...(totalCountLabel && {
          totalCountLabel
        })
      }
    }),
    ...(urlSorting && {
      sortingState: urlSorting.sorting
    }),
    ...(client.enabled && {
      sortingState: client.sorting,
      globalFilter: client.globalFilter,
      setGlobalFilter: client.setGlobalFilter
    }),
    ...(selection.enabled && {
      selectedRows,
      clearSelection: selection.clearSelection,
      // Ready to spread into <DataTable selection={...} />; add `actions` at the call site.
      selection: {
        selectedCount: selectedRows.length,
        onClear: selection.clearSelection,
        rowSelectionState: selection.rowSelectionState
      }
    }),
    ...(active.enabled && {
      activeRow: active.activeRow,
      setActiveRow: active.setActiveRow,
      // Consumed by <DataTable> straight from the spread.
      activeRowState: active.tableState
    }),
    ...(expansion.enabled && rowExpansion && {
      toggleRow,
      collapseAll: expansion.collapseAll,
      // Consumed by <DataTable> straight from the spread.
      expansion: {
        expandedState: expansion.expandedState,
        renderExpandedContent: rowExpansion.renderExpandedContent,
        getSubRows: rowExpansion.getSubRows,
        childColumns: rowExpansion.childColumns,
        onChildRowClick: rowExpansion.onChildRowClick,
        childRowHref: rowExpansion.childRowHref,
        ...(rowExpansion.expandOnRowClick && {
          toggleOnRowClick: toggleRow
        })
      }
    })
  };
}